import {
  BenefitBackground,
  DynamicIconType,
  FieldTypeLink,
} from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React from 'react';

import './Benefit.css';

import BenefitIcon from '../../benefitIcon';
import TextLink from '../../textLink';

export interface BenefitProps {
  headline: string;
  subheadline?: string;
  content: string;
  icon: string;
  background?: BenefitBackground;
  isMultiColor?: boolean;
  isNumeric?: boolean;
  textLinkText?: string;
  textLinkHref?: FieldTypeLink;
  numberOfColumns?: number;
}

const Benefit: React.FC<BenefitProps> = (props: BenefitProps) => {
  const {
    headline,
    subheadline,
    content,
    icon,
    background = BenefitBackground.DEFAULT,
    isMultiColor,
    isNumeric,
    textLinkText,
    textLinkHref,
    numberOfColumns = 4,
  } = props;

  const themes = {
    PURPLE: {
      wrapper: 'bg-purple80 monochrome:bg-grey90',
      headline: 'text-green60 monochrome:text-white',
      icon: 'text-green60 monochrome:text-white',
      content: 'text-white',
      textLink: 'text-white monochrome:text-white',
      fillLink: 'fill-white monochrome:fill-white',
    },
    GREEN: {
      wrapper: 'bg-green60 monochrome:bg-grey40',
      headline: 'text-purple80 monochrome:text-grey90',
      icon: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      textLink: 'text-purple80 monochrome:text-grey90',
      fillLink: 'fill-purple80 monochrome:fill-grey90',
    },
    WHITE: {
      wrapper: 'bg-white',
      headline: 'text-purple80 monochrome:text-grey90',
      icon: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      textLink: 'text-purple80 monochrome:text-grey90',
      fillLink: 'fill-purple80 monochrome:fill-grey90',
    },
    GREY: {
      wrapper: 'bg-grey10',
      headline: 'text-purple80 monochrome:text-grey90',
      icon: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      textLink: 'text-purple80 monochrome:text-grey90',
      fillLink: 'fill-purple80 monochrome:fill-grey90',
    },
    BLACK: {
      wrapper: 'bg-grey80',
      headline: 'text-green60 monochrome:text-white',
      icon: 'text-green60 monochrome:text-white',
      content: 'text-white',
      textLink: 'text-white monochrome:text-white',
      fillLink: 'fill-white monochrome:fill-white',
    },
    DEFAULT: {
      wrapper: 'bg-white',
      headline: 'text-purple80 monochrome:text-grey90',
      icon: 'text-green60 monochrome:text-grey60',
      content: 'text-grey100',
      textLink: 'text-grey100 monochrome:text-grey100',
      fillLink: 'fill-grey100 monochrome:fill-grey100',
    },
  };

  const themeBg =
    isMultiColor && background ? background : BenefitBackground.DEFAULT;

  const className = {
    wrapper: clsx(
      'relative lg:p-40px lg:w-full xl:pb-60px',
      isNumeric && 'p-40px',
      !isNumeric && 'p-30px',
      themes[themeBg].wrapper
    ),
    icon: clsx(themes[themeBg].icon, 'sm:mb-20px lg:mb-40px'),
    headline: clsx(
      'break-words font-headline h-auto',
      isNumeric && 'text-headline1',
      !isNumeric && 'text-headline4 mb-10px lg:mt-20px',
      themes[themeBg].headline
    ),
    subheadline: clsx(
      'break-words font-headline h-auto mb-20px text-headline4 lg:mb-40px',
      numberOfColumns === 3 && 'lg:min-h-[70px]',
      numberOfColumns === 4 && isNumeric && 'lg:min-h-[70px]',
      numberOfColumns === 3 && isNumeric && 'lg:min-h-[40px]',
      themes[themeBg].headline
    ),
    content: clsx(
      'text-p2',
      textLinkText && 'sm:mb-20px lg:mb-40px',
      themes[themeBg].content,
      themeBg === BenefitBackground.BLACK ||
        themeBg === BenefitBackground.PURPLE
        ? 'benefit-content--dark'
        : 'benefit-content--light'
    ),
    textLinkContent: 'lg:absolute sm:bottom-30px lg:bottom-40px',
  };

  const url = textLinkHref && getUrlFromStoryblokLink(textLinkHref);

  return (
    <div className={className.wrapper}>
      {!isNumeric && (
        <BenefitIcon
          icon={icon as DynamicIconType}
          customClassName={className.icon}
          isNumeric={isNumeric}
        />
      )}
      <h4 className={className.headline}>{headline}</h4>
      {subheadline && <h5 className={className.subheadline}>{subheadline}</h5>}
      {content && (
        <div
          dangerouslySetInnerHTML={{
            __html: content,
          }}
          className={className.content}
        ></div>
      )}
      {textLinkText && textLinkHref && (
        <div className={className.textLinkContent}>
          <TextLink
            text={textLinkText}
            href={url && parseSlashToURL(url)}
            target={textLinkHref?.target ?? '_self'}
            textColor={themes[themeBg].textLink}
            fillColor={themes[themeBg].fillLink}
            isDarkBg={
              (themeBg === BenefitBackground.BLACK ||
                themeBg === BenefitBackground.PURPLE) &&
              true
            }
          />
        </div>
      )}
    </div>
  );
};

export default Benefit;
